import React, { useState } from 'react'
import moment from 'moment'
import FChart from './FChart'
import BChart from './BChart'

const CHART_TABS = [
  { id: 'face', label: 'Face Charts', icon: 'ti ti-mood-smile', color: '#4680ff', bg: '#eef2ff' },
  { id: 'body', label: 'Body Charts', icon: 'ti ti-man',        color: '#2CA87F', bg: '#edfaf4' },
]

const SERVICES_PER_PAGE = 10

const Derma = ({
  patientId,
  patientDetails,
  // services
  services = [],
  availableServices = [],
  handleAddService,
  handlePrintClick,
  handleEditService,
  handleDeleteService,
  handleServiceStatusChange,
  // prescriptions
  prescriptions = [],
  recordLockHours,
  dateqq,
  handleAddPrescription,
  handleViewPrescription,
  handleEditPrescription,
  handleDeletePrescription,
}) => {
  const [chartTab, setChartTab]           = useState('face')
  const [serviceSearch, setServiceSearch] = useState('')
  const [servicePage, setServicePage]     = useState(1)
  const [expandedRow, setExpandedRow]     = useState(null)

  // ── Services derived ─────────────────────────────────────────────────────
  const safeServices = Array.isArray(services) ? services : []

  const filteredServices = safeServices.filter(service => {
    if (!serviceSearch) return true
    const svcData = availableServices.find(s => s.id === (service.serviceId ?? service.service_id))
    const name = svcData?.serviceName || service.serviceName || ''
    const code = svcData?.code || service.serviceCode || ''
    return (
      name.toLowerCase().includes(serviceSearch.toLowerCase()) ||
      code.toLowerCase().includes(serviceSearch.toLowerCase())
    )
  })

  const totalServicePages = Math.ceil(filteredServices.length / SERVICES_PER_PAGE) || 1
  const paginatedServices = filteredServices.slice(
    (servicePage - 1) * SERVICES_PER_PAGE,
    servicePage * SERVICES_PER_PAGE
  )

  // ── Prescriptions derived ─────────────────────────────────────────────────
  const filteredPrescriptions = prescriptions.filter(item => {
    if (dateqq) return moment(item.prescribed_at).format('YYYY-MM-DD') === dateqq
    return true
  })

  return (
    <div className="col-12 p-0" style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 13, fontFamily: 'inherit' }}>

      {/* ── Face / Body Charts (full width) ─────────────────────────────── */}
      <div className="card table-card patientCardHeader">
        <div className="tableHeader d-flex align-items-center gap-0" style={{ padding: 0, borderBottom: '1px solid #e8ecf0' }}>
          {CHART_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setChartTab(tab.id)}
              style={{
                flex: 1, justifyContent: 'center',
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', fontWeight: chartTab === tab.id ? 700 : 500,
                color: chartTab === tab.id ? tab.color : '#5B6B79',
                background: chartTab === tab.id ? tab.bg : 'transparent',
                border: 'none', cursor: 'pointer',
                borderBottom: chartTab === tab.id ? `2px solid ${tab.color}` : '2px solid transparent',
                marginBottom: -1, transition: 'all 0.15s',
              }}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>
        <div className="card-body" style={{ minHeight: 180 }}>
          {chartTab === 'face' && (
            <FChart patientId={patientId} patientDetails={patientDetails} />
          )}
          {chartTab === 'body' && (
            <BChart patientId={patientId} patientDetails={patientDetails} />
          )}
        </div>
      </div>

      {/* ── Services + Prescriptions row ─────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

        {/* ── Services (3/4) ──────────────────────────────────────────────── */}
        <div style={{ flex: 3, minWidth: 0 }}>
          <div className="card table-card patientCardHeader">
            <div className="tableHeader d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <h5 className="mb-0">Services</h5>
                <div className="tableSearch" style={{ position: 'relative', width: 220 }}>
                  <input
                    type="text"
                    placeholder="Search services..."
                    className="form-control"
                    value={serviceSearch}
                    onChange={e => { setServiceSearch(e.target.value); setServicePage(1) }}
                    style={{ paddingLeft: 35, paddingTop: 3, paddingBottom: 3, height: 30 }}
                  />
                  <span className="searchIcon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none"
                      viewBox="0 0 24 24" stroke="gray"
                      style={{ position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="bgBtn btn-sm d-flex align-items-center gap-1"
                  style={{ padding: '5px 12px' }}
                  onClick={handleAddService}
                >
                  Add Service
                </button>
              </div>
            </div>

            <div className="card-body p-0">
              <div className="table-responsive rounded-bottom">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                      <th>Name</th>
                      <th>Notes</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Added By</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Net Amt.</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedServices.length > 0 ? paginatedServices.map((service, index) => {
                      const serviceData  = availableServices.find(s => s.id === (service.serviceId ?? service.service_id))
                      const netAmount    = Number(service.netAmount ?? service.net_amount ?? 0)
                      const isCompleted  = ['complete', 'completed'].includes((service.status || '').toString().toLowerCase())
                      const name         = serviceData?.serviceName || service.serviceName || '-'
                      const description  = serviceData?.description || service.description || ''
                      const notes        = service.extra_notes || ''
                      const createdAt    = service.createdAt || service.created_at
                      const isLocked     = recordLockHours != null && createdAt
                        ? (Date.now() - new Date(createdAt).getTime()) > recordLockHours * 3600 * 1000
                        : false
                      const isPaid       = (service.billing_status || '') === 'Paid'
                      const editDisabled = isLocked
                      const delDisabled  = isLocked || isPaid
                      return (
                        <tr key={index}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {moment(createdAt).format('DD/MM/YY')}
                          </td>
                          <td title={description} style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {name}
                          </td>
                          <td title={notes} style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {notes || '-'}
                          </td>
                          <td style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {service?.doctor_name || service?.sent_by || '-'}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{netAmount} BHD</td>
                          <td>
                            {isCompleted ? (
                              <span className="badge bg-success">Done</span>
                            ) : (
                              <button
                                title="Mark Complete"
                                style={{ padding: '2px 6px', border: '1px solid var(--green)', borderRadius: 4, background: '#fff', color: 'var(--green)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'var(--green)'; e.currentTarget.style.color = '#fff' }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = 'var(--green)' }}
                                onClick={() => handleServiceStatusChange(service.id, 'Complete')}
                              >
                                ✓ Done
                              </button>
                            )}
                          </td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            <span
                              title={isLocked ? 'Record is locked' : 'Edit'}
                              style={{ cursor: editDisabled ? 'not-allowed' : 'pointer', color: '#4680ff', marginRight: 8, opacity: editDisabled ? 0.4 : 1 }}
                              onClick={() => !editDisabled && handleEditService(service)}
                            >
                              <i className="ti ti-edit" />
                            </span>
                            <span
                              title={isLocked ? 'Record is locked' : isPaid ? 'Cannot delete paid service' : 'Delete'}
                              style={{ cursor: delDisabled ? 'not-allowed' : 'pointer', color: '#dc3545', opacity: delDisabled ? 0.4 : 1 }}
                              onClick={() => !delDisabled && handleDeleteService(service.id, service.serviceId)}
                            >
                              <i className="ti ti-trash" />
                            </span>
                          </td>
                        </tr>
                      )
                    }) : (
                      <tr><td colSpan="7" className="text-center" style={{ color: '#aaa', padding: 20 }}>No services added yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalServicePages > 1 && (
                <nav className="tablepagination d-flex justify-content-end mt-3 mb-2">
                  <ul className="pagination mb-0">
                    <li className={`page-item ${servicePage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => servicePage > 1 && setServicePage(servicePage - 1)}>&laquo;</button>
                    </li>
                    <li className="pageNmbr">{`${servicePage} of ${totalServicePages}`}</li>
                    <li className={`page-item ${servicePage === totalServicePages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => servicePage < totalServicePages && setServicePage(servicePage + 1)}>&raquo;</button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>

        {/* ── Prescriptions (1/4) ─────────────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="card table-card patientCardHeader">
            <div className="tableHeader d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Prescriptions</h5>
              <div className="addBtnIcon" style={{ cursor: 'pointer' }} onClick={handleAddPrescription}>
                <i className="borderBtn ti ti-plus f-20" style={{ padding: '5px 12px' }} />
              </div>
            </div>
            <div className="card-body p-0 bg-white rounded-bottom">
              <div className="table-responsive rounded-bottom">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                      <th>Medicine</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPrescriptions.length > 0 ? filteredPrescriptions.map((plan, index) => {
                      const isLocked = recordLockHours != null && plan.prescribed_at
                        ? (Date.now() - new Date(plan.prescribed_at).getTime()) > recordLockHours * 3600 * 1000
                        : false
                      const firstMed = plan.medicines?.[0]?.medicine_name || '-'
                      const moreCount = (plan.medicines?.length || 1) - 1
                      return (
                        <tr key={index}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <div>{new Date(plan.prescribed_at).toLocaleDateString('en-GB')}</div>
                            <div style={{ color: '#888', fontSize: '0.85em' }}>
                              {plan.prescribed_at
                                ? new Date(plan.prescribed_at).toLocaleTimeString('en-GB', {
                                    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Bahrain',
                                  })
                                : ''}
                            </div>
                          </td>
                          <td style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={plan.medicines?.map(m => m.medicine_name).join(', ')}>
                            {firstMed}
                            {moreCount > 0 && (
                              <span style={{ color: '#4680ff', marginLeft: 4, fontSize: '0.85em' }}>+{moreCount}</span>
                            )}
                          </td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            <span title="View" style={{ cursor: 'pointer', color: '#5B6B79', marginRight: 6 }}
                              onClick={() => handleViewPrescription(plan.group_id)}>
                              <i className="ti ti-printer" />
                            </span>
                            <span title={isLocked ? 'Record is locked' : 'Edit'}
                              style={{ cursor: isLocked ? 'not-allowed' : 'pointer', color: '#4680ff', marginRight: 6, opacity: isLocked ? 0.4 : 1 }}
                              onClick={() => !isLocked && handleEditPrescription(plan)}>
                              <i className="ti ti-edit" />
                            </span>
                            <span title={isLocked ? 'Record is locked' : 'Delete'}
                              style={{ cursor: isLocked ? 'not-allowed' : 'pointer', color: '#dc3545', opacity: isLocked ? 0.4 : 1 }}
                              onClick={() => !isLocked && handleDeletePrescription(plan.group_id)}>
                              <i className="ti ti-trash" />
                            </span>
                          </td>
                        </tr>
                      )
                    }) : (
                      <tr><td colSpan="3" className="text-center" style={{ color: '#aaa', padding: 20 }}>N/A</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default Derma
